@file:OptIn(ExperimentalTime::class)

package com.macrofocus.eflux.ui.perspective

import com.macrofocus.common.json.JsonObject
import com.macrofocus.eflux.components.EFluxContext
import com.macrofocus.eflux.components.blurBackground
import com.macrofocus.eflux.network.risk.RiskTime
import com.macrofocus.eflux.toRawString
import com.macrofocus.eflux.ui.DateChooser
import com.macrofocus.eflux.ui.HorizontalSpacer
import com.macrofocus.eflux.ui.LiveSwitch
import com.macrofocus.eflux.ui.TimeSlider
import com.macrofocus.eflux.ui.data.DateTimeButton
import com.macrofocus.eflux.ui.data.risk.*
import kotlinx.browser.window
import mui.material.Stack
import mui.material.StackDirection
import mui.material.styles.Theme
import mui.material.styles.useTheme
import mui.system.SxProps
import mui.system.responsive
import mui.system.sx
import org.mkui.react.useProperty
import org.molap.dataframe.DataFrame
import org.molap.questdb.DateTimeHandling
import org.molap.questdb.readQuestDBJson
import react.*
import web.cssom.*
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

external interface ClockProps : Props {
}

val Clock: FC<*> = FC<ClockProps> {
    val context = useRequired(EFluxContext)
    val theme = useTheme<Theme>()

    val timeTracker = context.dataLoader.configuration.timeTracker

    val (maxDateTime, _) = useProperty(timeTracker.maxDateTime)
    val maxDateTimeRaw = maxDateTime.toRawString()

    val (getRiskTimes, setRiskTimes) = useState<List<RiskTime>>()

    useEffect(maxDateTimeRaw) {
        window.fetch("/db/riskTimes/${maxDateTimeRaw}").then {
            it.json().then { it ->
                val dataFrame = DataFrame.readQuestDBJson(it.unsafeCast<JsonObject>(), DateTimeHandling.Instant)
                val list = List<RiskTime>(dataFrame.rowCount) { i ->
                    RiskTime(
                        dataFrame.getValueAt(i, "risk_time") as Instant,
                        dataFrame.getValueAt(i, "lightning_hit") as Long,
                        dataFrame.getValueAt(i, "lightning_activity") as Long,
                        dataFrame.getValueAt(i, "lightning_expected") as Long,
                        dataFrame.getValueAt(i, "flooding") as Long,
                        dataFrame.getValueAt(i, "mudslide") as Long,
                        dataFrame.getValueAt(i, "wind_gusts") as Long,
                        dataFrame.getValueAt(i, "temperature") as Long,
                        if(dataFrame.columnIndex.contains("probability")) dataFrame.getValueAt(i, "probability") as? Double else null,
                        dataFrame.getValueAt(i, "logicalBranchIds") as String
                    )
                }.sortedByDescending { it.riskTime }
                setRiskTimes(list)
            }
        }
    }

    val customBackground: (SxProps<Theme>) -> Unit = { it ->
        it.blurBackground(theme)
    }

    LiveSwitch {
        this.timeTracker = timeTracker
        badge = true
    }

    DateChooser {
        this.timeTracker = timeTracker
    }

    HorizontalSpacer {}

    TimeSlider {
        this.timeTracker = timeTracker
        timeUnitChooser = true
        valueLabelDisplay = "auto"
        width = 10.vw
    }

    Stack {
        direction = responsive(StackDirection.column)
        asDynamic().justifyContent = JustifyContent.center
        sx {
            marginTop = 10.px
            marginBottom = 3.px
            columnGap = 8.px
        }

        if (getRiskTimes != null) {
            Stack {
                direction = responsive(StackDirection.column)
                spacing = responsive(2)

                sx {
                    maxHeight = 30.vh
                    overflowY = Auto.auto
                    paddingTop = 10.px
                    paddingLeft = 36.px
                }
                for (line in getRiskTimes) {
                    Stack {
                        direction = responsive(StackDirection.row)
                        asDynamic().justifyContent = JustifyContent.left
                        spacing = responsive(0.5)

                        DateTimeButton {
                            this.timeTracker = timeTracker
                            this.logicalBranchId = line.riskTime
                        }

                        if (line.temperature > 0) {
                            TemperatureBadge {
                                badgeContent = ReactNode(line.temperature.toString())
                            }
                        }

                        if (line.wind_gusts > 0) {
                            WindGustsBadge {
                                badgeContent = ReactNode(line.wind_gusts.toString())
                            }
                        }

                        if (line.mudslide > 0) {
                            MudslideBadge {
                                badgeContent = ReactNode(line.mudslide.toString())
                            }
                        }

                        if (line.flooding > 0) {
                            FloodingBadge {
                                badgeContent = ReactNode(line.flooding.toString())
                            }
                        }

                        if (line.lightning_hit > 0) {
                            LightningHitBadge {
                                badgeContent = ReactNode(line.lightning_hit.toString())
                            }
                        }

                        if (line.lightning_expected > 0) {
                            LightningExpectedBadge {
                                badgeContent = ReactNode(line.lightning_expected.toString())
                            }
                        }

                        if (line.lightning_activity > 0) {
                            LightningActivityBadge {
                                badgeContent = ReactNode(line.lightning_activity.toString())
                            }
                        }
                    }
                }
            }
        }
    }
}
